import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, MessageSquareReply, RotateCcw, Square } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { useBotChatQuery, useTaskBotStatusQuery } from '../../hooks/queries';
import { useAssignTaskBot, useCancelRun, useReviewTaskWork } from '../../hooks/mutations';
import useBotRunStreams from '../../hooks/useBotRunStreams';
import { botKeys } from '../../lib/queryKeys';
import ApprovalCard from './ApprovalCard';
import BotReply from './BotReply';
import { useTask } from '../../contexts/TaskContext';
import BotAvatar from './BotAvatar';
import { useBotLabel } from './botDisplay';
import { RUNNING } from './botActivity';
import { chatPath } from './chatLinks';

/**
 * The bot's work on a task, in the task detail: the task's thread with the bot (each
 * handover or request for changes, and the bot's work on it with its steps and result),
 * the same conversation the bot's chat list shows. While the bot works: Stop; actions
 * waiting for your OK; then its result waiting for review (approve and complete, or ask
 * for changes, which continues the thread).
 */
const TaskBotPanel = ({ taskId, bot }) => {
  const botLabel = useBotLabel();
  const { t } = useTranslation();
  const { openTaskDetail: onOpenTask } = useTask();
  const queryClient = useQueryClient();
  const { data } = useTaskBotStatusQuery(taskId, { enabled: !!bot });
  const run = data?.run;
  const { data: thread } = useBotChatQuery(data?.chat_id, { enabled: !!data?.chat_id });
  const threadRuns = useMemo(() => thread?.runs || [], [thread]);
  const live = useBotRunStreams(useMemo(() => [...threadRuns, ...(run ? [run] : [])], [threadRuns, run]));
  const cancel = useCancelRun();
  const review = useReviewTaskWork();
  const retry = useAssignTaskBot();
  const [asking, setAsking] = useState(false);
  const [feedback, setFeedback] = useState('');

  const messages = thread?.messages || [];
  if (!bot || !data || (!run && !data.review && !messages.length)) return null;
  const name = botLabel(bot);
  const approvals = data.approvals || [];
  const runsById = Object.fromEntries(threadRuns.map((r) => [r.id, r]));
  const working = run && RUNNING.has(run.status);
  const ended = run && (run.status === 'failed' || run.status === 'cancelled');

  const sendReview = (decision) => review.mutate(
    { taskId, decision, feedback: decision === 'changes' ? feedback : undefined },
    {
      onSuccess: () => {
        setAsking(false);
        setFeedback('');
        // Hide the review buttons right away; the refetch brings the new run.
        queryClient.setQueryData(botKeys.taskStatus(taskId), (old) => old && { ...old, review: null });
      },
      onError: () => toast.error(t('bots.chat.send_failed')),
    });

  // A task handed over before threads existed: its latest run, its result is a comment.
  const legacy = !data.chat_id && run ? (
    <BotReply run={run} live={live[run.id]} onOpenTask={onOpenTask} />
  ) : null;

  return (
    <div className="mx-4 my-3 rounded-lg border border-border bg-white/[0.03] p-3 space-y-3" data-testid="task-bot-panel">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <BotAvatar bot={bot} size={20} />
        <span className="font-medium text-foreground/80">{name}</span>
        {data.chat_id && thread?.chat && (
          <Link to={chatPath(data.chat_id)}
            className="ml-auto hover:text-foreground" data-testid="task-bot-open-chat">
            {t('bots.chat.chats_title')}
          </Link>
        )}
        {working && (
          <Button size="sm" variant="outline" className={`h-7 gap-1 ${data.chat_id && thread?.chat ? '' : 'ml-auto'}`}
            onClick={() => cancel.mutate(run.id)} disabled={cancel.isPending}>
            <Square className="w-3 h-3" />{t('bots.control.stop')}
          </Button>
        )}
      </div>

      {legacy}
      {messages.length > 0 && (
        <div className="space-y-3" data-testid="task-bot-thread">
          {messages.map((m) => (m.role === 'assistant' ? (
            <div key={m.id} className="min-w-0 text-sm text-foreground">
              <BotReply message={m} run={runsById[m.run_id]} live={live[m.run_id]} onOpenTask={onOpenTask} />
            </div>
          ) : (
            <div key={m.id} className="flex justify-end">
              <div className="max-w-[85%] bg-accent text-foreground rounded-2xl rounded-br-md px-3 py-2">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.content}</p>
              </div>
            </div>
          )))}
        </div>
      )}

      {approvals.length > 0 && (
        <div className="space-y-2">
          {approvals.map((a) => <ApprovalCard key={a.id} approval={a} botName={name} />)}
        </div>
      )}

      {data.review && !working && (
        <div className="space-y-2">
          <div className="text-sm text-foreground/80">{t('bots.control.review_title', { bot: name })}</div>
          {asking ? (
            <div className="space-y-2">
              <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={3} autoFocus
                placeholder={t('bots.control.review_placeholder')}
                className="w-full text-sm rounded-md border border-border bg-transparent px-2 py-1.5 text-foreground focus:outline-none focus:border-amber-500/60" />
              <div className="flex gap-2">
                <Button size="sm" className="h-7 bg-amber-500 text-black hover:bg-amber-400"
                  disabled={!feedback.trim() || review.isPending} onClick={() => sendReview('changes')}>
                  {t('bots.control.review_send', { bot: name })}
                </Button>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setAsking(false)}>{t('common.cancel')}</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" className="h-7 gap-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                disabled={review.isPending} onClick={() => sendReview('approve')}>
                <Check className="w-3.5 h-3.5" />{t('bots.control.review_approve')}
              </Button>
              <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setAsking(true)}>
                <MessageSquareReply className="w-3.5 h-3.5" />{t('bots.control.review_changes')}
              </Button>
            </div>
          )}
        </div>
      )}

      {ended && !data.review && (
        <Button size="sm" variant="outline" className="h-7 gap-1" disabled={retry.isPending}
          onClick={() => retry.mutate({ taskId, botId: bot.id })}>
          <RotateCcw className="w-3.5 h-3.5" />{t('bots.control.retry')}
        </Button>
      )}
    </div>
  );
};

export default TaskBotPanel;
